import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  chooseTheme,
  cn,
  DragScrollProvider,
  Icon,
  Toaster,
  initialsOf,
} from '@evogard/ui';
import type { Theme } from '@evogard/domain';
import {
  activeItem,
  activeModule,
  visibleModules,
  type MenuItem,
  type MenuModule,
} from '../menu.js';
import { useUnreadEntities } from '../lib/notifications.js';
import { Header } from './Header.js';
import { auth } from '../lib/auth.js';
import {
  useSessionContext,
  useSavePreferences,
  useSwitchTenant,
} from '../lib/session.js';
import { useRealtimeNotifications } from '../lib/realtime.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@evogard/ui/primitives';

/**
 * A moldura da interna: uma lateral navy de 272px e uma faixa clara no topo.
 *
 * ## O que mudou em 05/09, e por quê
 *
 * Até aqui a moldura eram DUAS colunas navy — um rail de 3.5rem com um ícone
 * por módulo, e ao lado a barra com o menu **do módulo aberto**. O desenho novo
 * troca as duas por uma coluna só, com todos os módulos abertos ao mesmo tempo
 * em grupos recolhíveis.
 *
 * A troca resolve o que o rail cobrava: para ver o que existe em Cobrança sem
 * sair de Demandas era preciso trocar de módulo e voltar.
 *
 * ## O desenho de 16/09, e o que ele mudou
 *
 * A lateral foi repintada e remedida: 272px de largura, a marca centrada num
 * cabeçalho de 108px, o módulo numa linha de 40px e o item numa de 33px, dentro
 * de um bloco recuado com linha-guia. As cores moram todas em `--cor-chrome-*`
 * (`packages/ui/src/theme.css`), com o hexadecimal do desenho anotado ao lado
 * de cada token e as duas que um piso de contraste obrigou a mover explicadas
 * ali.
 *
 * **A lateral é INTEGRADA, desde 07/10 — ela não flutua.** De 18/09 até aqui
 * ela era um painel solto: respiro dos quatro lados (`lg:gap-x-2` e `lg:p-2` no
 * grid), canto `lg:rounded-grande` e `lg:shadow-cartao`. Os três saíram, e a
 * faixa e a mesa perderam os mesmos três junto — eles eram UMA propriedade da
 * grade, não três decisões separadas.
 *
 * A lateral agora vai de borda a borda da janela, encostada no alto, no pé e na
 * faixa, e o que a separa do conteúdo é UMA linha (`lg:border-r
 * lg:border-chrome-borda`) em vez de 8px de página aparecendo entre as peças.
 *
 * O que a troca ganha é o que o nome diz: flutuando, três retângulos pousados
 * sobre um cinza leem como três cartões numa página; encostados, leem como uma
 * janela de aplicação — que é o que isto é.
 *
 * **O `overflow-hidden` FICA, embora o canto redondo que ele protegia tenha
 * ido.** Ele tinha dois trabalhos: impedir que a pílula do módulo aceso
 * desenhasse o canto quadrado dela por cima do canto redondo do painel, e
 * manter a pílula dentro da coluna. O primeiro deixou de existir; o segundo
 * continua.
 *
 * A peça continua sendo a MESMA na área de trabalho e dentro da gaveta do
 * telefone, e é por isso que respiro e borda estão no LAYOUT e nunca na
 * lateral: margem escrita nela daria duas larguras conforme quem a desenha.
 *
 * **E os grupos passaram a nascer FECHADOS** — só o do módulo em que a pessoa
 * está abre. Ver o cabeçalho de `Group`.
 *
 * ## A faixa do topo mora em `Header.tsx`, desde 16/09
 *
 * Aqui ficou o GRID — as duas colunas, o respiro, o pulo para o conteúdo e o
 * que é da sessão. A trilha, o sino, o menu da conta e a gaveta do telefone
 * saíram para o vizinho, junto com o estado da gaveta, que nunca foi do
 * `Layout`: ele só o segurava porque o `Sheet` estava escrito aqui dentro.
 *
 * Este arquivo tinha 961 linhas e respondia por duas coisas de naturezas
 * diferentes. O que `Layout` continua dando ao `Header` é o que só ele sabe: a
 * base ativa, o módulo aberto, o endereço, a pessoa, e a própria lateral —
 * porque no telefone ela é o conteúdo da gaveta, e montá-la duas vezes daria as
 * duas anatomias que a decisão 3 do `DESIGN.md` proíbe.
 *
 * ## A moldura é a JANELA, e quem rola é a mesa
 *
 * Acima de `lg` a grade tem `h-dvh` e `overflow-hidden`: a moldura ocupa a
 * janela e não rola nunca. A lateral é `h-full` dentro dela, a faixa é
 * `lg:static` no alto da coluna, e o `<main>` é o único que rola
 * (`lg:overflow-y-auto`). Com a moldura integrada não há mais vão nenhum para
 * manter no lugar — as três peças encostam, e as linhas entre elas ficam onde
 * estão em qualquer posição da rolagem.
 *
 * **Isto substitui a `sticky`, e o motivo é que ela nunca pôde acertar os dois
 * momentos.** A lateral era `lg:sticky lg:top-1` com `h-[calc(100dvh-0.5rem)]`
 * — os números do respiro de 4px, de quando a grade era `lg:p-1`. Com os 8px
 * de hoje ela nascia a 8px do topo e o fundo caía em `100dvh` exatos (encostada
 * embaixo), e ao rolar prendia a 4px e sobrava vão dos dois lados; a faixa,
 * `sticky top-0`, subia os 8px e encostava no alto da tela. Medido no navegador
 * em 17/09/2026. Casar `top` e altura com o respiro resolveria a aritmética e
 * deixaria a moldura ainda assentando pela rolagem; com a janela fixa não há
 * respiro para nenhuma peça copiar.
 *
 * Altura automática na lateral continua proibida pelo mesmo motivo de antes:
 * numa lista de 53 demandas o rodapé com a conta ia parar a 3285px do topo.
 *
 * **O `relative` na grade e na mesa é o que fecha a janela de verdade.**
 * `overflow-hidden` não segura filho `absolute` — o bloco que contém um
 * `absolute` é o ancestral POSICIONADO mais próximo, e sem nenhum ele mede
 * contra o documento e passa por fora do corte. Medido em 17/09/2026 em
 * `/tickets`: o `<label>` `sr-only` do "por página" — `sr-only` É `absolute` —
 * caía a 904px, o `html` ia a 905px, e a tela ganhava uma segunda barra, a que
 * envolve tudo. Ela rolava 185px de nada, porque o corpo já cabia na janela.
 *
 * O sintoma aponta para longe da causa: a barra é do documento e quem a criou é
 * um rótulo invisível de uma tabela. Com a mesa posicionada, todo `absolute`
 * sem âncora passa a medir contra ela e a rolar junto com o conteúdo — e é por
 * isso que o `relative` está nas DUAS: o que nasce fora da mesa (a lateral, a
 * faixa, o pulo para o conteúdo) fica contido pela grade.
 *
 * **No telefone nada disso vale**: lá quem rola é o documento e a faixa volta a
 * ser `sticky top-0`, que é o que deixa a barra do navegador recolher.
 *
 * ## No telefone não há lateral
 *
 * 272px fixos é o que sobra de um telefone. Abaixo de `lg` a moldura vira a
 * faixa do topo com a trilha, e a lateral inteira mora numa gaveta — quem a
 * desenha é o `Header`, e é por isso que ele recebe a lateral por `sidebar`.
 */
export function Layout() {
  // o `beforeLoad` da rota já garantiu que existe sessão antes de chegar aqui
  const { data: context } = useSessionContext();

  /*
   * O tema da CONTA, aplicado assim que o contexto chega.
   *
   * Sem isto a tela de preferências grava e ninguém lê: num computador novo, ou
   * depois de limpar o navegador, a pessoa entrava e via o tema do sistema
   * apesar de ter escolhido escuro. É o mesmo defeito que o portal teve, com a
   * diferença de que aqui não custa requisição nenhuma — a preferência já vem
   * no contexto (`plataforma-08`), e o que falta é escrevê-la no documento.
   *
   * `chooseTheme` e não `applyTheme`: além de pintar, regrava o eco local,
   * que é o que faz a próxima abertura neste aparelho já nascer certa e não
   * piscar.
   */
  useEffect(() => {
    if (context) chooseTheme(context.preferences.theme);
  }, [context]);
  // uma conexão por sessão — ver o cabeçalho de `lib/realtime.tsx`
  useRealtimeNotifications();
  const navigate = useNavigate();
  const client = useQueryClient();
  const path = useRouterState({ select: (s) => s.location.pathname });

  /*
   * A lateral RECOLHIDA — 64px de ícones, desde 07/10.
   *
   * **O estado é local e não é gravado, e isso é a regra da casa e não
   * preguiça.** É a mesma decisão do grupo aberto/fechado, algumas linhas
   * abaixo: gravá-lo é preferência de pessoa, e preferência de pessoa neste
   * produto mora no servidor (`preferencesSchema`), nunca num `localStorage`
   * que seria a segunda fonte de verdade que o `theme.ts` existe para não ter.
   * O preço é que a lateral volta estendida a cada recarga; a saída é uma
   * chave em `preferencesSchema`, que é trabalho de contrato, API e migration.
   *
   * Vale só acima de `lg`. No telefone a lateral mora numa gaveta que já abre e
   * fecha inteira — recolher uma gaveta a 64px é oferecer dois gestos para a
   * mesma coisa, e a gaveta é o que o dedo alcança.
   */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (!context) return null;

  const modules = visibleModules({
    permissions: context.permissions,
    consultant: context.consultant,
  });
  const open = activeModule(path, modules);
  const current = activeItem(path, open);

  async function signOut() {
    await auth.signOut();
    client.clear();
    void navigate({ to: '/sign-in', replace: true });
  }

  const sidebar = (
    <Sidebar
      modules={modules}
      open={open}
      current={current}
      context={context}
      toSignOut={signOut}
    />
  );

  return (
    /*
     * `data-print="folha"` marca a moldura, e não é enfeite: ela é um grid de
     * duas colunas, e esconder a navegação deixa UM item num grid que ainda tem
     * os dois trilhos — o conteúdo inteiro cai na coluna de 272px. Medido no
     * navegador em 02/09/2026, imprimindo o relatório de demandas: as métricas
     * saíram sobrepostas e as tabelas com 56px de largura. O `@media print` de
     * `theme.css` desfaz o grid por esta marca.
     */
    <div
      data-print="folha"
      className={cn(
        'relative grid min-h-dvh grid-rows-[auto_1fr] lg:h-dvh lg:min-h-0 lg:grid-rows-[minmax(0,1fr)] lg:overflow-hidden',
        /*
         * As duas larguras escritas por extenso, e não `lg:grid-cols-[${w}]`:
         * o Tailwind só emite a classe que LÊ no fonte, e uma montada em tempo
         * de execução desenharia coluna nenhuma — sem erro, com a lateral
         * cobrindo a tela. É a mesma armadilha de `COLOR_CLASSES` no `Kanban`.
         */
        sidebarCollapsed
          ? 'lg:grid-cols-[4rem_minmax(0,1fr)]'
          : 'lg:grid-cols-[17rem_minmax(0,1fr)]',
      )}
    >
      {/*
       * Teclado é caminho de primeira classe: sem isto, chegar ao conteúdo
       * custa passar pela lateral inteira, em toda troca.
       */}
      <a
        href="#conteudo"
        className="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:left-4 focus-visible:top-4 focus-visible:z-30 focus-visible:rounded-borda focus-visible:bg-superficie focus-visible:px-4 focus-visible:py-2 focus-visible:text-sm focus-visible:font-medium"
      >
        Pular para o conteúdo
      </a>

      {/*
       * A da área de trabalho é a ÚNICA que recolhe. A da gaveta vem da
       * variável `sidebar` logo acima, sempre estendida, e é a mesma peça —
       * duas anatomias para a mesma coisa é o que a decisão 3 do `DESIGN.md`
       * proíbe, e o que se passa aqui não é outra anatomia: é a mesma, mais
       * estreita.
       */}
      <div data-print="fora" className="max-lg:hidden">
        <Sidebar
          modules={modules}
          open={open}
          current={current}
          context={context}
          toSignOut={signOut}
          collapsed={sidebarCollapsed}
          onExpand={() => setSidebarCollapsed(false)}
        />
      </div>

      <div className="flex min-w-0 flex-col lg:min-h-0">
        <Header
          tenant={context.currentTenant.nome}
          module={open}
          page={activeLabel(open, current)}
          path={path}
          user={context.user}
          theme={context.preferences.theme}
          notificationSound={context.preferences.notificationSound}
          sidebar={sidebar}
          toSignOut={signOut}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed((v) => !v)}
        />

        <Toaster />

        {/*
         * A MESA — o `<main>` virou superfície em 17/09.
         *
         * Até aqui ele era transparente e encostava na faixa do topo: medido no
         * navegador, `rgba(0, 0, 0, 0)` e zero de vão. Tinha recuo dos lados (o
         * `p-4`/`sm:p-6` de dentro) e nada em cima, então a área útil não era
         * uma superfície — era o `body` aparecendo entre a lateral navy e o
         * conteúdo.
         *
         * **Ela parou de flutuar E deixou de ser branca, em 07/10.**
         *
         * Tinha `mt-2`, `lg:rounded-cartao` e `lg:shadow-cartao` — os três do
         * painel pousado sobre o cinza da página. Com a moldura integrada (ver
         * o docblock da lateral) eles passaram a desenhar contra bordas que não
         * existem mais: uma tira de 8px da cor da página entre a faixa e o
         * conteúdo, e cantos redondos encostando na beira da janela. Saíram os
         * três.
         *
         * E com eles foi o `bg-superficie`. **O que se via como "o sistema é
         * todo branco" nunca foi o token do fundo** — ele já era cinza desde
         * sempre; era esta mesa branca cobrindo a área inteira, com o cinza
         * aparecendo só nos 8px entre as peças. Tirado o vão, o fundo não
         * apareceria em lugar nenhum: a mesa passou a SER a página
         * (`bg-fundo`), que é como o desenho novo a pinta.
         *
         * O degrau de 17/09 inverteu de dono e continua sendo um só: era
         * `fundo` atrás e `superficie` aqui; agora é `fundo` aqui e
         * `superficie` no `Card`, que volta a se levantar por COR em cima da
         * mesa tingida em vez de depender só da borda e da sombra.
         *
         * **E é ele quem rola, acima de `lg`** — `overflow-y-auto` com
         * `min-h-0`, que é o que faz a caixa encolher dentro da grade de altura
         * fixa em vez de esticá-la. Sem o `min-h-0` o conteúdo empurra a coluna
         * para além da janela e o `overflow-hidden` da moldura corta o fim da
         * lista. O `scrollbar-gutter` reserva a canaleta: sem ela a mesa muda de
         * largura entre uma tela curta e uma longa.
         *
         * **Sem `max-w` próprio, de propósito.** Ele tinha `max-w-[97.5rem]` e
         * a faixa acima não tem nenhum: num monitor de 2560px a mesa ficava
         * 700px mais estreita que o cabeçalho dela, encostada à esquerda. Teto
         * de largura é da CONTA de cada tela (`<Page measure>`, cobrado por
         * `tools/design/check.mjs`), e não da moldura — que existe para ser a
         * mesma caixa de cima a baixo.
         *
         * **Não há mais curvatura aqui, em nenhuma largura**, e com ela foi-se
         * a razão pela qual havia duas: o canto só existia acima de `lg`, onde
         * a grade abria o vão que o fazia aparecer, porque no telefone a coluna
         * encosta nas bordas da tela e arredondar deixaria quatro triângulos da
         * cor do `body` nos cantos. Com a moldura integrada a área útil encosta
         * nas bordas nas DUAS larguras, e o caso do telefone virou o único
         * caso.
         */}
        <main
          id="conteudo"
          className="relative w-full flex-1 bg-fundo p-4 pb-12 sm:p-6 sm:pb-12 lg:min-h-0 lg:overflow-y-auto lg:[scrollbar-gutter:stable] lg:rolagem-mesa"
        >
          {/* Arrastar para rolar de lado: pedido para o sinistro (30/10/2026), e só ele. */}
          <DragScrollProvider on={path.startsWith('/claims')}>
            <Outlet />
          </DragScrollProvider>
        </main>
      </div>
    </div>
  );
}

/** O rótulo do item aberto — o último degrau da trilha. */
function activeLabel(module: MenuModule | null, current: string | null): string | null {
  if (!module || !current) return null;
  for (const section of module.sections) {
    for (const item of section.items) if (item.to === current) return item.label;
  }
  return null;
}

/**
 * A lateral: marca, base ativa, os módulos em grupos, e a conta no rodapé.
 *
 * É a MESMA peça na área de trabalho e dentro da gaveta do telefone. Duas
 * anatomias para a mesma coisa é o defeito que a decisão 3 do `DESIGN.md`
 * proíbe — e foi o que a moldura anterior tinha, com uma faixa de pastilhas no
 * telefone que não existia em lugar nenhum da área de trabalho.
 */
function Sidebar({
  modules,
  open,
  current,
  context,
  toSignOut,
  collapsed = false,
  onExpand,
}: {
  modules: MenuModule[];
  open: MenuModule | null;
  current: string | null;
  context: NonNullable<ReturnType<typeof useSessionContext>['data']>;
  toSignOut: () => Promise<void>;
  /** 64px de ícones. Só a instância da área de trabalho passa isto. */
  collapsed?: boolean;
  /** Estende de volta — o clique num ícone de módulo com a lateral estreita. */
  onExpand?: () => void;
}) {
  /*
   * Lido AQUI e não dentro de cada `Group`: é a mesma consulta do sino, e
   * chamar o hook por módulo faria sete assinaturas do mesmo cache para
   * responder a mesma pergunta.
   */
  const unreadEntities = useUnreadEntities();

  return (
    <nav
      /*
       * O `id` é o alvo do `aria-controls` do botão que recolhe, na faixa.
       *
       * Ele fica na instância da ÁREA DE TRABALHO e na da gaveta — são duas
       * montagens da mesma peça e o id repetiria. Só uma está no documento por
       * vez: a da gaveta só existe enquanto o `Sheet` está aberto, e ele é
       * `lg:hidden`, exatamente a largura em que o botão não existe.
       */
      id="navegacao-principal"
      aria-label="Navegação principal"
      data-chrome
      className="flex h-full flex-col overflow-hidden bg-chrome text-chrome-texto lg:border-r lg:border-chrome-borda"
    >
      {/*
       * O cabeçalho da marca: 108px de altura mínima, a marca CENTRADA e mais
       * respiro em cima do que embaixo (20px contra 12px), como a referência.
       *
       * `min-h` e não `h`: a marca tem proporção fixa e a coluna não muda de
       * largura, então a caixa nunca precisa crescer — mas uma altura travada
       * cortaria a marca em quem aumenta o tamanho da fonte do navegador, e
       * cortar a marca é o defeito que ninguém reporta e todo mundo vê.
       *
       * O botão de tema morava aqui, à direita. Ele saiu para o rodapé em
       * 16/09: a marca centrada ocupa a linha inteira, e um botão ao lado a
       * descentraria. Ao lado de "Sair" ele fica junto do resto do que é da
       * CONTA, que é onde ele pertence.
       */}
      <div
        className={cn(
          'flex items-center justify-center pb-3 pt-5',
          collapsed ? 'min-h-0 px-2' : 'min-h-[6.75rem] px-3',
        )}
      >
        {/*
         * O logotipo, e não o `Wordmark` em texto.
         *
         * O desenho de 05/09 põe a marca horizontal branca aqui, e ela é a
         * única superfície do produto onde o navy é fundo cheio — que é
         * exatamente para o que essa versão do arquivo de marca existe. O
         * `Wordmark` continua respondendo pela marca em texto, onde ela é
         * título (a tela de entrar) ou onde não há fundo escuro.
         *
         * `w-[12.5rem]` são os 200px da referência; a altura sai da proporção do
         * arquivo e cabe folgada nos 76px úteis do cabeçalho.
         *
         * Desde 03/11/2026 é a marca "Proteção Veicular", no lugar da "Seguros".
         * O arquivo original tem o "evo" e o subtítulo em azul-marinho, que
         * sumiriam no navy daqui: a versão desta lateral troca o azul por
         * branco e mantém o laranja, como a horizontal branca que ela substitui.
         */}
        {/*
         * Recolhida, a marca vira o SÍMBOLO — `nav-icon.png`, o mesmo quadrado
         * de 256px que já é o ícone da aba. A horizontal tem 200px de largura e
         * não cabe em 64px: encolhê-la até caber deixaria a palavra "evogard"
         * em 6px, que é marca ilegível, não marca pequena.
         */}
        <img
          src={collapsed ? '/nav-icon.png' : '/marca-protecao-veicular-branco.png'}
          alt="Evogard Proteção Veicular"
          className={cn(
            'max-w-full object-contain',
            collapsed ? 'size-8' : 'w-[12.5rem]',
          )}
        />
      </div>

      {/*
       * O seletor de base SOME quando a lateral recolhe, e não vira um ícone.
       *
       * Ele é um `select` com o nome da praça e quantas existem — recolhido a
       * 64px sobraria a pastilha "CE", que diz a sigla e esconde que há outra
       * base e que dá para trocar. Um controle que não anuncia o que faz é pior
       * do que um controle ausente: a trilha do topo continua dizendo em que
       * base a pessoa está, e estender a lateral é um clique.
       */}
      {!collapsed && (
        <div className="px-[1.125rem] pb-3">
          <p className="rotulo mb-1.5 text-chrome-texto-suave">Base ativa</p>
          <TenantSwitcher
            current={context.currentTenant}
            tenants={context.tenants}
            initials={context.setting.brandInitials}
            initialsByTenant={context.tenantInitials}
          />
        </div>
      )}

      {/*
       * Sem respiro à ESQUERDA: a pílula do módulo aceso encosta na borda
       * esquerda da coluna e para antes da direita, com o canto arredondado só
       * desse lado. É o que faz a lateral parecer uma aba e não uma lista de
       * botões.
       *
       * **A lista rola e a barra dela não desenha**, desde 17/09. A moldura é a
       * janela e quem rola é a mesa (ver o cabeçalho deste arquivo): uma
       * segunda barra na lateral põe dois trilhos na mesma tela, e o de cá é o
       * da navegação, não o do conteúdo.
       *
       * Rolar continua: roda do mouse, gesto e teclado. **Ela precisa rolar** —
       * medido no navegador em 17/09/2026 numa janela de 900px, com o grupo da
       * Plataforma aberto a lista pede 708px e tem 623px. Travá-la cortaria o
       * rodapé com a conta, que é a única saída da sessão.
       *
       * Com a barra fora, o `scrollbar-gutter: stable` saiu junto e o respiro
       * virou `pr-2`. Ele existia para o defeito oposto: barra que aparece e
       * some tira ~11px do CONTEÚDO nos dois sentidos, a mesma lateral fica com
       * duas larguras úteis, e abrir um grupo encolhe os rótulos de todos os
       * outros. Barra que nunca desenha não tira nada, e a pílula para no mesmo
       * lugar com e sem rolagem — que era o que a calha comprava.
       */}
      <div className="flex-1 overflow-y-auto py-3 pr-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {modules.map((m) => (
          <Group
            key={m.id}
            module={m}
            open={m.id === open?.id}
            current={current}
            unreadEntities={unreadEntities}
            collapsed={collapsed}
            onExpand={onExpand}
          />
        ))}
      </div>

      {/*
       * Recolhido, o rodapé é só a pastilha — o nome, o e-mail, o botão de tema
       * e o de sair saem com os rótulos.
       *
       * **Sair continua alcançável**, e por isso isto não é perda: o menu da
       * conta, no canto direito da faixa, tem o mesmo gesto e está sempre lá.
       * O mesmo vale para o tema. Se um dia o menu da conta sair da faixa,
       * esconder "Sair" aqui vira um beco, e este comentário é o aviso.
       */}
      <div
        className={cn(
          'flex items-center border-t border-chrome-borda py-3',
          collapsed ? 'justify-center px-2' : 'gap-2.5 px-[1.125rem]',
        )}
      >
        <span
          aria-hidden={!collapsed}
          title={collapsed ? context.user.nome : undefined}
          className="grid size-8 shrink-0 place-items-center rounded-borda bg-chrome-alto text-xs font-bold text-chrome-texto"
        >
          {initialsOf(context.user.nome)}
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[0.8125rem] font-semibold text-chrome-texto">
                {context.user.nome}
              </span>
              {/*
               * O desenho põe o PAPEL da pessoa aqui ("Administrador"). O contexto
               * da sessão não tem papel — a autorização é recurso × ação concedido
               * a grupo, e uma pessoa pode estar em mais de um. Escrever um papel
               * aqui seria a tela afirmando o que o código não sabe; o e-mail é o
               * que identifica a conta quando o nome se repete, e é verdade.
               */}
              <span className="block truncate text-[0.6875rem] text-chrome-texto-suave">
                {context.user.email}
              </span>
            </span>
            <ThemeToggle theme={context.preferences.theme} />
            <button
              type="button"
              onClick={() => void toSignOut()}
              title="Sair"
              className="grid size-11 shrink-0 place-items-center rounded-borda text-chrome-texto-suave transition-colors hover:bg-chrome-alto hover:text-chrome-texto lg:size-7"
            >
              <Icon nome="sair" size={16} label="Sair" />
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

/**
 * O botão que troca o tema, no rodapé da lateral.
 *
 * Ele morava no alto, ao lado da marca, e desceu em 16/09 — o desenho novo dá
 * 190px à marca e não sobra coluna para os dois. O rodapé já é a faixa da
 * CONTA, e trocar de tema é preferência de pessoa: ele fica ao lado de "Sair",
 * que é o outro gesto da mesma natureza.
 *
 * Ele não é um atalho novo: grava pela MESMA mutação da tela de preferências e
 * do menu da conta, e por isso os três lugares nunca discordam do que foi
 * salvo. O que ele acrescenta é o gesto de um clique — abrir uma tela para
 * trocar de cor é atrito suficiente para a pessoa desistir e continuar com a
 * interface que a incomoda.
 *
 * Alterna entre claro e escuro. `sistema` continua existindo e continua sendo
 * escolhível no menu da conta e na tela de preferências: um botão de dois
 * estados não tem como oferecer três, e forçá-lo a girar entre os três faria
 * este botão ser o único lugar do produto onde o clique não é previsível.
 */
function ThemeToggle({ theme }: { theme: Theme }) {
  const save = useSavePreferences();
  const isDark = theme === 'escuro';
  const next: Theme = isDark ? 'claro' : 'escuro';

  return (
    <button
      type="button"
      title={isDark ? 'Usar o tema claro' : 'Usar o tema escuro'}
      onClick={() => {
        chooseTheme(next);
        // a reversão fica em quem pintou antes da resposta — igual à tela
        save.mutate({ theme: next }, { onError: () => chooseTheme(theme) });
      }}
      className="grid size-11 shrink-0 place-items-center rounded-borda text-chrome-texto-suave transition-colors hover:bg-chrome-alto hover:text-chrome-texto lg:size-7"
    >
      <Icon
        nome={isDark ? 'sol' : 'lua'}
        size={16}
        label={isDark ? 'Usar o tema claro' : 'Usar o tema escuro'}
      />
    </button>
  );
}

/**
 * A base ativa: sigla, nome e quantas existem — e um menu para trocar.
 *
 * ## Menu, e não mais o `<select>` nativo (09/10/2026)
 *
 * Até aqui o controle era um `<select>` transparente por cima da face, para
 * ganhar o seletor nativo do telefone e a digitação por inicial. O preço
 * apareceu na tela: a lista que o navegador abre é pintada pelo SISTEMA, em
 * branco e azul, fora do desenho da lateral — e foi pedido trocar. Agora é o
 * mesmo `DropdownMenu` do menu da conta, com um grupo de rádio (uma base
 * marcada). O teclado continua: setas, Enter, Esc e a digitação por inicial,
 * que o Radix dá no menu.
 *
 * `aria-label="Base"` no gatilho, e não `<label>` visível: o rótulo "BASE
 * ATIVA" já está acima, em versalete, e dois nomes acessíveis para o mesmo
 * controle é o defeito que `internal.tenant.spec.ts` registra.
 */
function TenantSwitcher({
  current,
  tenants,
  initials,
  initialsByTenant,
}: {
  current: { id: number; nome: string };
  tenants: { id: number; nome: string }[];
  initials: string | undefined;
  initialsByTenant: { tenantId: number; initials: string }[];
}) {
  const toggle = useSwitchTenant();
  /* a sigla de cada base no menu — a mesma pastilha do gatilho */
  const initialsOf = (id: number) =>
    initialsByTenant.find((t) => t.tenantId === id)?.initials ?? '\u00a0';
  const alone = tenants.length <= 1;

  const face = (
    <>
      {/*
       * A sigla vem da configuração da BASE, não do código. `undefined` só
       * acontece antes de o contexto chegar — e mostrar a letra do padrão faria
       * a sigla trocar na frente de quem olha.
       */}
      <InitialsBadge value={initials ?? '\u00a0'} />

      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-[0.8125rem] font-semibold text-chrome-texto">
          {current.nome}
        </span>
        <span className="block text-[0.6875rem] text-chrome-texto-suave">
          {alone ? 'a sua única base' : `${tenants.length} bases disponíveis`}
        </span>
      </span>
    </>
  );

  const box =
    'flex w-full items-center gap-2.5 rounded-borda border border-chrome-borda bg-chrome-alto px-2.5 py-2 transition-colors';

  if (alone) return <div className={box}>{face}</div>;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Base"
        disabled={toggle.isPending}
        className={cn(
          box,
          'cursor-pointer outline-none hover:border-chrome-primaria focus-visible:border-chrome-primaria data-[state=open]:border-chrome-primaria',
          toggle.isPending && 'opacity-60',
        )}
      >
        {face}
        <Icon nome="seta-baixo" size={16} className="shrink-0 text-chrome-texto-suave" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        collisionPadding={12}
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
      >
        <DropdownMenuRadioGroup
          value={String(current.id)}
          onValueChange={(value) => {
            const id = Number(value);
            if (id !== current.id) toggle.mutate(id);
          }}
        >
          {tenants.map((t) => (
            <DropdownMenuRadioItem
              key={t.id}
              value={String(t.id)}
              className="gap-2.5 pointer-coarse:py-3"
            >
              <InitialsBadge value={initialsOf(t.id)} />
              {t.nome}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A pastilha da sigla da base — no gatilho e em cada item do menu. */
function InitialsBadge({ value }: { value: string }) {
  return (
    <span
      aria-hidden
      className="grid size-6 shrink-0 place-items-center rounded-pequeno bg-primaria text-[0.6875rem] font-extrabold text-primaria-contraste"
    >
      {value}
    </span>
  );
}

/**
 * Um módulo na lateral: o cabeçalho recolhível e os itens.
 *
 * ## Os grupos nascem FECHADOS, menos o do módulo aberto
 *
 * É o desenho de 16/09, e é a segunda vez que esta linha muda — vale registrar
 * as duas, porque cada uma pagou um preço diferente.
 *
 * Até 05/09 a lateral abria só o grupo do módulo atual **e fechava os outros a
 * cada navegação**: quem fechava um grupo o via reabrir sozinho. Em 05/09 todos
 * passaram a nascer abertos, e o custo foi a coluna inteira — 29 itens em 7
 * grupos — sempre estendida.
 *
 * Agora o estado inicial é fechado e o do módulo em que a pessoa está abre, e a
 * diferença para o desenho de antes de 05/09 é que **um grupo aberto à mão
 * continua aberto**: o `useEffect` abaixo só ABRE, nunca fecha. Quem quiser ver
 * Cobrança sem sair de Demandas abre Cobrança e ela fica.
 *
 * O preço é o clique a mais para chegar a um módulo que não é o atual, e quem
 * o anuncia é o chevron à direita do cabeçalho: ele diz, antes do clique, que
 * ali há algo para abrir. Módulo aberto o vira para cima.
 *
 * O estado é local e não é gravado: gravá-lo é preferência de pessoa, e
 * preferência de pessoa neste produto mora no servidor — inventar um
 * `localStorage` para isto criaria a segunda fonte de verdade que o `theme.ts`
 * existe para não ter.
 */
function Group({
  module,
  open,
  current,
  unreadEntities,
  collapsed = false,
  onExpand,
}: {
  module: MenuModule;
  open: boolean;
  current: string | null;
  /** as entidades de `app.notifications` com aviso não lido — ver `menu.ts` */
  unreadEntities: Set<string>;
  /** a lateral está a 64px: só o ícone, e os itens não cabem */
  collapsed?: boolean;
  onExpand?: () => void;
}) {
  const [expanded, setExpanded] = useState(open);

  /*
   * Navegar para dentro de um módulo abre o grupo dele. Sem isto, quem chega a
   * uma demanda por link vê a lateral sem sinal nenhum de onde está.
   *
   * Só ABRE: fechar aqui faria a lateral desfazer, a cada navegação, o grupo
   * que a pessoa abriu para olhar outro módulo.
   */
  useEffect(() => {
    if (open) setExpanded(true);
  }, [open]);

  const itemHasNews = (item: MenuItem) =>
    Boolean(item.notificationEntity && unreadEntities.has(item.notificationEntity));

  /*
   * O grupo RECOLHIDO herda a novidade dos itens que ele esconde.
   *
   * Os grupos nascem fechados desde 16/09, e sem isto o ponto laranja do item
   * só existiria para quem já estivesse com o módulo aberto — ou seja, para
   * quem menos precisa ser avisado.
   */
  const groupHasNews = module.sections.some((s) => s.items.some(itemHasNews));

  return (
    <div className="mb-0.5">
      <button
        type="button"
        /*
         * Recolhida, o clique ESTENDE a lateral e abre este módulo.
         *
         * Sem isto ele alternaria um estado que ninguém vê — os itens não cabem
         * em 64px —, e o botão seria um clique morto: o pior tipo de controle,
         * porque parece quebrado em vez de parecer ausente.
         */
        onClick={() => {
          if (collapsed) {
            onExpand?.();
            setExpanded(true);
            return;
          }
          setExpanded((v) => !v);
        }}
        /*
         * Recolhida, o botão não controla região visível nenhuma, então
         * `aria-expanded` mentiria. E o rótulo sai da tela, então o nome
         * acessível precisa vir por `aria-label` — sem ele o leitor de tela
         * anuncia sete botões sem nome.
         */
        aria-expanded={collapsed ? undefined : expanded}
        aria-label={collapsed ? module.label : undefined}
        title={collapsed ? module.label : undefined}
        /*
         * A linha do desenho: 40px de altura, 18px de respiro lateral, 14px de
         * corpo em peso 500, e 14px entre o ícone e o rótulo.
         *
         * `min-h-11` em ponteiro grosso e não mais respiro no geral: o alvo é
         * 44px onde há dedo, e calcular `py` a partir da altura de linha erra
         * por meio pixel — foi assim que o item ficou em 43.5px e passou a
         * régua arredondando. O piso é o piso; `internal.touch.spec.ts` mede a
         * caixa, não a conta.
         */
        className={cn(
          'relative flex h-10 w-full items-center text-left text-sm text-chrome-texto transition-colors pointer-coarse:h-auto pointer-coarse:min-h-11',
          collapsed ? 'justify-center px-0' : 'gap-4 rounded-r-borda pl-6 pr-2.5',
          /*
           * A pílula marca o módulo em que a pessoa ESTÁ, e não o que está
           * aberto. São perguntas diferentes: abrir Cobrança para olhar não
           * muda onde ela está, e pintar o aberto faria a lateral responder
           * "você está aqui" para um grupo que ela só espiou.
           */
          open ? 'bg-chrome-ativo font-semibold' : 'font-medium hover:bg-chrome-alto',
        )}
      >
        {/*
         * O ícone do MÓDULO, e não a seta. Ele é o que faz a coluna se varrer
         * sem ler palavra por palavra — a seta virou o indicador de
         * aberto/fechado, à direita, onde ela não disputa a primeira coluna.
         */}
        <Icon nome={module.icon} size={20} className="shrink-0 text-chrome-texto-suave" />
        {!collapsed && <span className="min-w-0 flex-1 truncate">{module.label}</span>}
        {/*
         * O ponto do grupo RECOLHIDO — e some quando ele abre, porque aí o
         * ponto do item diz a mesma coisa com mais precisão. Dois pontos
         * acesos sobre o mesmo aviso fariam parecer que são dois.
         *
         * **A cor sai do fundo REAL, não do estado.** Sobre `chrome` o laranja
         * mede 4.5:1; sobre `chrome-ativo` — a pílula do módulo em que a pessoa
         * está — mede 1.97:1, e sumiria dentro da peça que ele existe para
         * marcar. É a lição do `Wordmark` e do `Metric`, onde uma prop
         * respondia "pequeno" e "sobre fundo escuro" ao mesmo tempo e o
         * subtítulo saiu branco sobre branco. Nenhum teste pega: cor que some
         * por contraste não quebra asserção.
         */}
        {groupHasNews && (collapsed || !expanded) && (
          /*
           * Sem `sr-only` aqui dentro, pelo mesmo motivo do `ItemNav`: texto
           * dentro do gatilho entra no nome acessível, e o botão do módulo
           * deixaria de se chamar "Demandas". Quem não vê a cor abre o grupo e
           * encontra a descrição no item, onde ela é acionável — "há novidade
           * em algum lugar aqui dentro" não diz o que fazer.
           */
          <span
            aria-hidden
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              open ? 'bg-chrome-texto' : 'bg-chrome-primaria',
              /* recolhida não há linha para ocupar: ele vira selo sobre o ícone */
              collapsed && 'absolute right-3 top-2.5',
            )}
          />
        )}
        {/*
         * O chevron NU, sem o disco — 07/10, pelo desenho de referência.
         *
         * Ele vinha dentro de um disco `bg-chrome-texto/40` de 18px, e o disco
         * pesava mais que a informação: numa coluna de sete módulos eram sete
         * bolinhas cinzas competindo com os ícones dos módulos, que são o que
         * se varre. O desenho usa só o traço, em cinza-azulado e um pouco mais
         * grosso — `#6F84A0` com `stroke-width: 2`, que aqui são
         * `chrome-texto-suave` e `--traco-icone: 2`.
         *
         * O `path` continua sendo o `seta-baixo` do catálogo e NÃO o do
         * desenho: o de lá é 2px mais estreito, e `seta-baixo` é usado em doze
         * lugares — tabela, seletor, filtros, escolha de permissão. Trocar o
         * desenho do ícone por causa de uma tela mudaria as outras onze sem
         * ninguém pedir.
         */}
        {!collapsed && (
          <Icon
            nome="seta-baixo"
            size={16}
            className={cn(
              /* no mesmo tempo do deslize, para a seta e o bloco chegarem juntos */
              'shrink-0 text-chrome-texto-suave transition-transform duration-200 ease-saida [--traco-icone:2]',
              expanded && 'rotate-180',
            )}
          />
        )}
      </button>

      {/*
       * O DESLIZE, e por que ele anima a altura (decisão do dono, 25/09/2026).
       *
       * Abrir e fechar eram um corte seco: o bloco aparecia inteiro e sumia
       * inteiro. O bloco agora fica sempre montado e desliza — a linha da grade
       * vai de `0fr` a `1fr`, que é a altura do conteúdo sem medir nada em
       * JavaScript, com o conteúdo esmaecendo e descendo 4px junto.
       *
       * É a exceção declarada ao "nunca anima propriedade de layout" do
       * `DESIGN.md`: aqui o layout que muda É a informação — o que abriu empurra
       * o que está embaixo, e ver o empurrão é o que diz de onde veio. 200ms, na
       * curva de saída da casa. Quem pediu movimento reduzido ao sistema
       * continua com o corte seco: o `theme.css` zera as transições para ele.
       *
       * `inert` quando fechado, e não só a altura zero: sem ele, os itens
       * escondidos continuariam recebendo o Tab e sendo lidos pelo leitor de
       * tela — a pessoa andaria por links que não vê.
       */}
      <div
        inert={!expanded || collapsed}
        className={cn(
          /* `visibility` entra na transição e muda de uma vez: no FIM do
             fechamento e no COMEÇO da abertura. Fechado, o bloco some da
             árvore de acessibilidade também — altura zero sozinha não tira o
             link dela, e o e2e (`getByRole`) o acharia. */
          'grid transition-[grid-template-rows,opacity,visibility] duration-200 ease-saida',
          expanded
            ? 'visible grid-rows-[1fr] opacity-100'
            : 'invisible grid-rows-[0fr] opacity-0',
          /*
           * `hidden` e não só `inert`: a 64px o bloco recuado tem 33px de
           * margem, e a linha-guia desenhava para fora da coluna — visto no
           * navegador, uma coluna de letras cortadas ao lado dos ícones.
           *
           * **Depois do `grid`, e isso é a correção.** Ele estava antes, e o
           * `cn` (`tailwind-merge`) resolve conflito pela ÚLTIMA classe: com
           * `hidden` primeiro, `grid` vencia e o `hidden` sumia do resultado
           * sem erro nenhum. `display` contra `display` é conflito, e a ordem
           * aqui é a regra, não o gosto.
           */
          collapsed && 'hidden',
        )}
      >
        <div
          className={cn(
            'min-h-0 overflow-hidden transition-transform duration-200 ease-saida',
            expanded ? 'translate-y-0' : '-translate-y-1',
          )}
        >
          {/*
           * O bloco recuado, com a linha-guia à esquerda.
           *
           * `ml-[2.0625rem]` põe a linha aos 33px da borda — o recuo da
           * referência —, e a borda esquerda É a linha: um `<div>` com `border-l`
           * desenha exatamente a altura do que ele contém, que é o que um traço
           * absoluto com altura calculada erraria a cada item novo.
           *
           * A cor é `chrome-texto/40`. Ela era a mesma do disco que embrulhava
           * o chevron acima, e o disco saiu em 07/10 — a linha-guia ficou com a
           * cor por conta própria, que é o tom em que um traço de apoio some
           * sem desaparecer sobre o navy.
           */}
          <div className="my-0.5 ml-[2.0625rem] border-l border-chrome-texto/40">
            <ul className="flex flex-col">
              {module.sections.map((section, i) => (
                <li key={section.label ?? `secao-${i}`}>
                  {/*
                   * A seção continua existindo dentro do grupo.
                   *
                   * A referência não tem este nível — os módulos dela são planos.
                   * Os nossos não são: Plataforma tem dez itens em três seções, e
                   * achatá-las transformaria "Acesso", "Configuração" e "Minha
                   * conta" numa lista única de dez linhas sem hierarquia. Aplicar
                   * um desenho não é apagar informação que ele não tinha.
                   */}
                  {section.label && (
                    <p className="pb-1 pl-4 pt-2.5 text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-chrome-texto-suave/80">
                      {section.label}
                    </p>
                  )}
                  <ul className="flex flex-col">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        <ItemNav
                          item={item}
                          current={item.to === current}
                          unread={itemHasNews(item)}
                        />
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Um item da lateral — dentro do bloco recuado, na linha-guia.
 *
 * ## O que mudou em 16/09
 *
 * Ele tinha o ÍCONE do item à frente e uma borda esquerda de 2px no laranja da
 * marca quando aceso. Na referência o subitem não tem ícone: tem um **ponto** —
 * cheio quando é a tela aberta, um aro vazado quando não é — e o rótulo em peso
 * 600. O ícone saiu daqui e do `menu.ts` junto: campo que nenhuma tela desenha
 * é campo que envelhece sem ninguém notar.
 *
 * **O laranja também saiu, e não foi escolha de gosto.** `chrome-primaria`
 * sobre `chrome-ativo` — o fundo novo do módulo aceso — dá 1.97:1: o acento
 * sumiria dentro da peça que ele existe para marcar, e nenhum teste pegaria,
 * porque cor que some por contraste não quebra asserção.
 *
 * O rótulo é branco nos dois estados, como na referência; o que muda é o peso e
 * o ponto. Quem carrega "em que MÓDULO eu estou" é a pílula do cabeçalho do
 * grupo, medida em `theme.test.ts` pelo par `chrome-texto sobre chrome-ativo`.
 *
 * O texto começa aos 65px da borda da coluna: 33px até a linha-guia, 16px de
 * recuo, o ponto de 6px e 10px de intervalo.
 */
function ItemNav({
  item,
  current,
  unread,
}: {
  item: MenuItem;
  current: boolean;
  unread: boolean;
}) {
  /*
   * A novidade é DESCRIÇÃO, nunca nome.
   *
   * Um `sr-only` dentro do `<Link>` entra no nome acessível, e o nome do item
   * deixaria de ser "Demandas" para virar "Demandas novidade não lida" — quem
   * navega por voz pede o link pelo nome, e o nome passaria a mudar conforme
   * houvesse aviso. Foi o que aconteceu na primeira versão disto, e quem pegou
   * foi `internal.tickets.spec.ts`, que procura o item por
   * `{ name: 'Demandas', exact: true }`.
   *
   * `aria-describedby` diz a mesma coisa no lugar certo: o nome continua o
   * mesmo, e a novidade é lida DEPOIS dele, como a cor é vista depois da
   * palavra. O elemento referenciado fica FORA do link, senão volta ao nome.
   */
  const newsId = `news-${item.to}`;

  return (
    <>
      <Link
        to={item.to}
        aria-describedby={unread ? newsId : undefined}
        /*
         * `exact`: com ele, o `aria-current` que o TanStack acrescenta sozinho só
         * aparece quando o endereço é igual ao do item — e aí coincide com o
         * nosso. Numa tela de detalhe o dele não aparece e o nosso marca o item
         * que a contém, que é a resposta certa.
         */
        activeOptions={{ exact: true }}
        aria-current={current ? 'page' : undefined}
        className={cn(
          'flex h-[2.0625rem] items-center gap-2.5 rounded-r-borda pl-4 pr-2.5 text-[0.8125rem] text-chrome-texto transition-colors pointer-coarse:h-auto pointer-coarse:min-h-11',
          current ? 'font-semibold' : 'hover:bg-chrome-alto',
        )}
      >
        {/*
         * O ponto, e agora ele responde DUAS perguntas.
         *
         * "Onde estou" continua sendo o cheio contra o aro vazado, como na
         * referência. "Tem novidade" é a COR: laranja quando há aviso não lido
         * daquela entidade, branco quando não.
         *
         * São dimensões separadas de propósito — a tela atual com novidade
         * desenha um ponto laranja CHEIO, e uma tela que não é a atual com
         * novidade desenha um ponto laranja cheio também. O aro vazado é sempre
         * "sem novidade e não estou aqui": quem tem aviso ganha o ponto sólido
         * porque um aro laranja de 6px é menos visível do que o aro branco que
         * substituiria.
         *
         * **O contraste está medido.** Os dois únicos fundos sob este item são
         * `chrome` (repouso) e `chrome-alto` (ponteiro), e `theme.test.ts` já
         * cobra `chrome-primaria` sobre os dois — 4.5:1 e 3:1. O item aceso NÃO
         * ganha `chrome-ativo`: aquele fundo é da pílula do módulo, acima, onde o
         * laranja daria 1.97:1.
         *
         * `aria-hidden` some com a metade "onde estou", que o `aria-current` já
         * diz. A metade "tem novidade" entra no nome acessível do link, abaixo —
         * cor não se ouve.
         */}
        <span
          aria-hidden
          className={cn(
            'size-1.5 shrink-0 rounded-full',
            unread
              ? 'bg-chrome-primaria'
              : current
                ? 'bg-chrome-texto'
                : 'border border-chrome-texto/50',
          )}
        />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
      </Link>
      {/* o que o ponto laranja diz, para quem não vê cor — ver o comentário acima */}
      {unread && (
        <span id={newsId} className="sr-only">
          novidade não lida
        </span>
      )}
    </>
  );
}
